import React, { useEffect, useState } from 'react';

import { AllMessageTypes } from '../../zod';
import BaseResponse from './types/BaseResponse';
import BaseResponseWithAnalysis from './types/BaseResponseWithAnalysis';
import DefaultMessage from './types/DefaultMessage';
import ErrorMessage from './types/ErrorMessage';
import TracerNodeMessage from './types/TracerNodeMessage';
import UserDataMessage from './types/UserDataMessage';
import useChatPanelStore from 'stateManagement/store/useChatPanelStore';

import { useGetSessionById } from 'stateManagement/queries/services/aiChat/aiChatQueries';

import MessageHeader from './utils/MessageHeader';

/**
 * Checks if a message starts with "Instructions:"
 * @param {string} content - The message content to check
 * @returns {boolean} True if the message starts with "Instructions:"
 */
const isInstructionsMessage = (content) => {
  if (typeof content !== 'string') return false;
  return content.trim().startsWith('Instructions:');
};

/**
 * Main component for rendering all chat messages
 * @param {Object} props - Component props
 * @param {Array} props.messages - Array of message objects
 * @param {string} props.sessionId - ID of the current chat session
 * @param {Object} props.user - User information object
 * @param {string} props.status - Status of the chat session
 */
const ChatMessages = ({
  messages,
  sessionId,
  user: { firstName, lastName },
  status,
}) => {
  const [processedMessages, setProcessedMessages] = useState([]);
  const { data: chatData } = useGetSessionById(sessionId);
  const chatSessionId = useChatPanelStore((state) => state.chatSessionId);

  // Process messages to ensure they have the correct format
  useEffect(() => {
    if (!messages || messages.length === 0) {
      setProcessedMessages([]);
      return;
    }

    const transformedMessages = messages.map(message => {
      if (message.role === 'assistant' && typeof message.content === 'string') {
        // The 'analyze current page' workflow returns a JSON string without a separator.
        // We try to parse it directly. This will only succeed on the final, complete message.
        try {
          const parsedJson = JSON.parse(message.content);
          if (parsedJson && typeof parsedJson === 'object' && parsedJson.messageText) {
            return {
              ...message,
              messageType: '7-analyze-current-page',
              contentAsJson: parsedJson,
            };
          }
        } catch (e) {
          // This is expected during streaming; the content is not yet valid JSON.
          // We'll proceed to the next check.
        }

        // Other workflows use a ':::done' separator.
        if (message.content.includes(':::done')) {
          const [messageType, contentStr] = message.content.split(':::');
          try {
            const contentAsJson = JSON.parse(contentStr);
            return {
              ...message,
              messageType,
              contentAsJson,
              content: contentStr, // Keep the string content for reference if needed
            };
          } catch (e) {
            // The content after the separator is not valid JSON. Return original message.
            console.error('Failed to parse content after :::done separator', e);
            return message;
          }
        }
      }
      // Return the original message if it's not an assistant message or doesn't match the criteria.
      return message;
    });
    setProcessedMessages(transformedMessages);
  }, [messages]);

  /**
   * Renders the appropriate message component based on message role and type
   * @param {Object} message - The message object to render
   * @returns {JSX.Element} The appropriate message component
   */
  const renderMessageComponent = (message) => {
    if (message.role === 'user' && isInstructionsMessage(message.content)) {
      return <UserDataMessage message={message} />;
    }

    // while streaming default to standard message
    if (status === 'streaming') {
      return <DefaultMessage message={message} />;
    }

    // get the message type
    const messageTypeParse = AllMessageTypes.safeParse(message.messageType);

    // if not one of the messageType, default to standard message
    if (!messageTypeParse.success) {
      return <DefaultMessage message={message} />;
    }

    // Handle specific message types
    switch (messageTypeParse.data) {
      case 'error':
        return <ErrorMessage />;

      case '0-default':
        return <BaseResponse content={message.contentAsJson} />;

      case '1-full-wallet-profile':
      case '2-wallet-kyc':
      case '3-analyze-txn-trace-call':
      case '4-full-txn-decode':
      case '5-full-contract-analysis':
        return <BaseResponseWithAnalysis content={message.contentAsJson} />;

      case '6-tracer-next-node':
        return <TracerNodeMessage content={message.contentAsJson} />;

      case '7-analyze-current-page':
        return <BaseResponse content={message.contentAsJson} />;

      default:
        return <DefaultMessage message={message} />;
    }
  };

  // Only render if the session matches the current chat session
  if (chatData?.sessionId !== chatSessionId) {
    return null;
  }

  // If no messages, show placeholder
  if (processedMessages.length === 0) {
    return (
      <div className='text-center text-500 fw-medium p-4'>No Messages</div>
    );
  }

  return (
    <div>
      {processedMessages.map((message) => (
        <div
          key={message.id}
          className={`d-flex align-items-center ${
            message.role === 'user'
              ? 'justify-content-end'
              : 'justify-content-start'
          } mb-2 ${message.role === 'user' ? 'w-75' : ''}`}
          style={{
            maxWidth: '100%',
            marginLeft: message.role === 'user' ? 'auto' : '0',
          }}
        >
          <div>
            {/* Message header with sender info */}
            <MessageHeader
              role={message.role}
              firstName={firstName}
              lastName={lastName}
              className={message.role === 'user' ? 'text-end' : ''}
            />

            {/* Message content container */}
            <div
              className={`${
                message.role !== 'system' &&
                !isInstructionsMessage(message.content)
                  ? `px-3 py-1 m-1 ${
                      message.role === 'user'
                        ? 'bg-primary text-white'
                        : 'bg-200'
                    } rounded`
                  : ''
              }`}
              style={{
                maxWidth: '100%',
                wordWrap: 'break-word',
              }}
            >
              {renderMessageComponent(message)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ChatMessages;
